import { useEffect, useState } from "react";

/**
 * How many pixels of the layout viewport the on-screen keyboard covers.
 *
 * The viewport meta sets `interactive-widget=resizes-content`, which makes
 * Android shrink the layout viewport so a bottom-anchored composer stays
 * visible on its own. iOS Safari ignores it and only shrinks the *visual*
 * viewport, leaving the layout untouched — so a growing composer slides behind
 * the keyboard. Comparing the two viewports gives the overlap to compensate,
 * and returns 0 wherever the browser already handled it.
 */
export const useKeyboardInset = (): number => {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const measure = () => {
      const covered =
        window.innerHeight - (viewport.height + viewport.offsetTop);
      // Sub-pixel noise and rubber-band scrolling both produce tiny values.
      setInset(covered > 1 ? Math.round(covered) : 0);
    };

    measure();
    viewport.addEventListener("resize", measure);
    viewport.addEventListener("scroll", measure);
    return () => {
      viewport.removeEventListener("resize", measure);
      viewport.removeEventListener("scroll", measure);
    };
  }, []);

  return inset;
};
